// src/pages/ReportsScreen.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../api';
import { fromZonedTime, toZonedTime, format } from 'date-fns-tz';
import { Printer, Clock, Info, ChevronDown, Edit, Send, Filter, X, ChevronUp, Settings2, Loader2, Plus, AlertTriangle, Coffee } from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/ReportsScreen.css';

import SplitPayrollModal from '../components/payroll/SplitPayrollModal'; 
import SummaryReportModal from '../components/reports/SummaryReportModal'; 
import { generateReportPDF } from '../components/reports/ReportGenerator';

const SERVER_TIMEZONE = 'America/Los_Angeles';

const calculateDateRange = (filter, customDates, timeZone = 'America/Los_Angeles') => {
    const now = new Date();
    const zonedNow = toZonedTime(now, timeZone); 

    const year = zonedNow.getFullYear();
    const month = zonedNow.getMonth();
    
    const toLocalISOString = (dateObj) => {
        return format(dateObj, 'yyyy-MM-dd', { timeZone });
    };

    switch (filter) {
        case 'firstHalf': 
            return { startDate: toLocalISOString(new Date(year, month, 1)), endDate: toLocalISOString(new Date(year, month, 15)) };
        case 'secondHalf':
            const lastDay = new Date(year, month + 1, 0);
            return { startDate: toLocalISOString(new Date(year, month, 16)), endDate: toLocalISOString(lastDay) };
        case 'week':
            const day = zonedNow.getDay();
            const diffToMonday = zonedNow.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(zonedNow);
            monday.setDate(diffToMonday);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return { startDate: toLocalISOString(monday), endDate: toLocalISOString(sunday) };
        case 'today': 
            return { startDate: toLocalISOString(zonedNow), endDate: toLocalISOString(zonedNow) };
        case 'month':
            const firstDayOfMonth = new Date(year, month, 1);
            const lastDayOfMonth = new Date(year, month + 1, 0);
            return { startDate: toLocalISOString(firstDayOfMonth), endDate: toLocalISOString(lastDayOfMonth) };
        case 'custom':
            if (!customDates.start || !customDates.end) { return { error: 'Incomplete dates' }; }
            return { startDate: customDates.start, endDate: customDates.end };
        default: 
            return { error: 'Invalid date filter selected.' };
    }
};

const calculateOvertimeSummary = (entries, thresholdHours = 8) => {
    if (!entries || entries.length === 0) return {};

    const userDailyTotals = {}; 
    entries.forEach(entry => {
        const start = new Date(entry.start_time);
        const end = entry.end_time ? new Date(entry.end_time) : new Date();
        let durationMillis = end - start;
        
        // Subtract the meal deduction from OT calculation
        if (entry.meal_deducted > 0) {
            durationMillis -= (entry.meal_deducted * 60 * 1000);
        }
        if (durationMillis < 0) durationMillis = 0;

        const dateKey = start.toDateString(); 
        const userKey = entry.employee_name;

        if (!userDailyTotals[userKey]) userDailyTotals[userKey] = {};
        if (!userDailyTotals[userKey][dateKey]) userDailyTotals[userKey][dateKey] = 0;
        userDailyTotals[userKey][dateKey] += durationMillis;
    });

    const userOvertimeMap = {};
    const thresholdMillis = thresholdHours * 60 * 60 * 1000;

    Object.keys(userDailyTotals).forEach(user => {
        let totalUserOTMillis = 0;
        const days = userDailyTotals[user];
        Object.values(days).forEach(dailyTotal => {
            if (dailyTotal > thresholdMillis) {
                totalUserOTMillis += (dailyTotal - thresholdMillis);
            }
        });

        if (totalUserOTMillis > 0) {
            const h = Math.floor(totalUserOTMillis / 3600000);
            const m = Math.floor((totalUserOTMillis % 3600000) / 60000);
            userOvertimeMap[user] = `${h}h ${m.toString().padStart(2, '0')}m`;
        }
    });

    return userOvertimeMap;
};

const ReportScreen = ({ user }) => {
    // --- STATE ---
    const [properties, setProperties] = useState([]);
    const [usersForFilter, setUsersForFilter] = useState([]);
    const [timeEntries, setTimeEntries] = useState([]);
    const [summary, setSummary] = useState(null);
    
    // Filter State
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [dateFilter, setDateFilter] = useState('today');
    const [customDate, setCustomDate] = useState({ start: '', end: '' });

    // UI State
    const [isLoading, setIsLoading] = useState(true); 
    const [isGenerating, setIsGenerating] = useState(false); 
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
    const [expandedEntryId, setExpandedEntryId] = useState(null);
    
    // Modals State
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false);
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    
    // Add Entry State
    const [isAddEntryModalOpen, setIsAddEntryModalOpen] = useState(false);
    const [addForm, setAddForm] = useState({ employeeId: '', date: '', startTime: '', endTime: '', notes: '' });

    const [reportDateRange, setReportDateRange] = useState({ start: '', end: '' });
    const [payrollData, setPayrollData] = useState(null);

    const [userTimezone, setUserTimezone] = useState('America/Los_Angeles');
    const [userTimezonePref, setUserTimezonePref] = useState(null);
    
    // Form for editing existing entry
    const [editForm, setEditForm] = useState({ date: '', startTime: '', endTime: '' });

    // Refs
    const abortControllerRef = useRef(null);
    const hasInitialized = useRef(false);

    // Permissions
    const canEdit = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);
    const canViewAll = user && user.role === 'SuperAdmin';
    const canSendToAccountant = user && ['SuperAdmin', 'Owner', 'Manager'].includes(user.role);

    const periodType = ['firstHalf', 'secondHalf'].includes(dateFilter) 
        ? (dateFilter === 'firstHalf' ? 'FIRST_HALF' : 'SECOND_HALF') 
        : 'CUSTOM';
    const isPayrollPeriod = ['firstHalf', 'secondHalf'].includes(dateFilter);

    const generateAcronym = (name) => {
        if (!name || typeof name !== 'string') return '';
        const firstWord = name.split(' ')[0];
        return `(${firstWord.toLowerCase()})`;
    };

    const handleToggleExpand = (entryId) => {
        setExpandedEntryId(prevId => (prevId === entryId ? null : entryId));
    };

    // --- MAIN FETCH FUNCTION ---
    const fetchReportData = async (propId, userId, filterType, customRange, propertiesOverride = null) => {
        const currentProp = propId ?? selectedProperty;
        const currentUser = userId ?? selectedUser;
        const currentFilter = filterType ?? dateFilter;
        const currentCustom = customRange ?? customDate;

        if (currentFilter === 'custom') {
            if (!currentCustom.start || !currentCustom.end) {
                setTimeEntries([]);
                setSummary(null);
                setPayrollData(null);
                return; 
            }
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setIsGenerating(true);
        
        const dateRange = calculateDateRange(currentFilter, currentCustom, userTimezone);
        
        if (dateRange.error) { 
            if (dateRange.error !== 'Incomplete dates') toast.error(dateRange.error); 
            setIsGenerating(false); 
            return; 
        }
        
        setReportDateRange({ start: dateRange.startDate, end: dateRange.endDate });

        const filters = { 
            propertyId: currentProp || null, 
            employeeId: currentUser || null, 
            startDate: dateRange.startDate, 
            endDate: dateRange.endDate 
        };

        try {
            // 1. Fetch RAW data from API
            const { punches: rawEntries } = await api.getWorkSummary(filters, { signal });
            if (signal.aborted) return; 

            const effectiveProperties = propertiesOverride || properties;
            
            // 2. UI-LEVEL PROCESSING: Apply Meal Deductions
            const processedEntries = rawEntries.map(entry => {
                const propObj = effectiveProperties.find(p => p.id === parseInt(entry.property_id));
                const isCancelled = entry.start_time === entry.end_time;
                let mealDeductedMins = 0;
                let finalDurationStr = entry.duration; 
                
                if (!isCancelled && entry.start_time && entry.end_time && propObj && propObj.settings_auto_meal_enabled) {
                    const start = new Date(entry.start_time);
                    const end = new Date(entry.end_time);
                    const rawMillis = end - start;
                    const thresholdMillis = parseFloat(propObj.settings_auto_meal_threshold || 6) * 3600000;
                    
                    if (rawMillis >= thresholdMillis) {
                        mealDeductedMins = parseInt(propObj.settings_auto_meal_minutes || 30);
                        let finalMillis = rawMillis - (mealDeductedMins * 60000);
                        if (finalMillis < 0) finalMillis = 0;
                        
                        const h = Math.floor(finalMillis / 3600000);
                        const m = Math.floor((finalMillis % 3600000) / 60000);
                        finalDurationStr = `${h}h ${m.toString().padStart(2, '0')}m`;
                    }
                }
                
                return {
                    ...entry,
                    payable_duration: finalDurationStr,
                    meal_deducted: mealDeductedMins
                };
            });

            // 3. UI-LEVEL PROCESSING: Recalculate Summary Totals
            let enrichedSummary = {};
            processedEntries.forEach(entry => {
                if (!enrichedSummary[entry.employee_name]) {
                     enrichedSummary[entry.employee_name] = {
                         employee_user_id: entry.user_id,
                         employee_name: entry.employee_name,
                         total_millis: 0
                     };
                }
                if (entry.start_time && entry.end_time && entry.start_time !== entry.end_time) {
                     const start = new Date(entry.start_time);
                     const end = new Date(entry.end_time);
                     let millis = (end - start) - (entry.meal_deducted * 60000);
                     if (millis > 0) enrichedSummary[entry.employee_name].total_millis += millis;
                }
            });

            Object.keys(enrichedSummary).forEach(name => {
                const millis = enrichedSummary[name].total_millis;
                const h = Math.floor(millis / 3600000);
                const m = Math.floor((millis % 3600000) / 60000);
                enrichedSummary[name].total_hours = `${h}h ${m.toString().padStart(2, '0')}m`;
            });

            // 4. Calculate Overtime (Uses updated UI data)
            const propObj = effectiveProperties.find(p => p.id === parseInt(currentProp));
            const isOtEnabled = propObj && (propObj.settings_overtime_enabled === 1 || propObj.settings_overtime_enabled === true);
            const otThreshold = propObj ? (propObj.settings_overtime_threshold || 8) : 8;

            if (isOtEnabled) {
                const otMap = calculateOvertimeSummary(processedEntries, otThreshold);
                Object.keys(enrichedSummary).forEach(employeeName => {
                    if (otMap[employeeName]) enrichedSummary[employeeName].overtime_hours = otMap[employeeName];
                });
            }

            setTimeEntries(processedEntries);
            setSummary(enrichedSummary);
            setExpandedEntryId(null);
            setPayrollData(null);

            if (currentProp && ['firstHalf', 'secondHalf'].includes(currentFilter)) {
                try {
                    const statement = await api.getPaymentStatement({
                        propertyId: currentProp,
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate,
                        periodType: currentFilter === 'firstHalf' ? 'FIRST_HALF' : 'SECOND_HALF'
                    });
                    if (!signal.aborted) setPayrollData(statement);
                } catch (draftError) {
                    if (!signal.aborted) setPayrollData(null);
                }
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                toast.error(err.message);
            }
        } finally {
            if (!signal.aborted) {
                setIsGenerating(false);
            }
        }
    };
    
    useEffect(() => {
        if (isLoading || !hasInitialized.current) return;
        fetchReportData();
    }, [selectedProperty, selectedUser, dateFilter, customDate, userTimezone]); 

    useEffect(() => {
        if (isEditModalOpen && editingEntry) {
            const zonedStart = toZonedTime(editingEntry.start_time, userTimezone);
            const zonedEnd = editingEntry.end_time 
                ? toZonedTime(editingEntry.end_time, userTimezone) 
                : toZonedTime(new Date(), userTimezone); 
            
            setEditForm({
                date: format(zonedStart, 'yyyy-MM-dd', { timeZone: userTimezone }),
                startTime: format(zonedStart, 'HH:mm', { timeZone: userTimezone }),
                endTime: editingEntry.end_time ? format(zonedEnd, 'HH:mm', { timeZone: userTimezone }) : ''
            });
        }
    }, [isEditModalOpen, editingEntry?.id, userTimezone]);

    useEffect(() => {
        const initializeScreen = async () => {
            setIsLoading(true);
            try {
                const [props, preferences] = await Promise.all([
                    api.getProperties(),
                    api.getUserPreferences()
                ]);

                setProperties(props);

                const safePrefs = Array.isArray(preferences) ? preferences : [];
                const getPref = (key) => {
                    const p = safePrefs.find(x => x.preference_key === key);
                    return p ? p.preference_value : null; 
                };

                const savedTz = getPref('preferred_timezone');
                setUserTimezonePref(savedTz);

                let initialPropId = '';
                const savedPropId = getPref('default_property_id');
                
                if (savedPropId && props.some(p => p.id.toString() === savedPropId.toString())) {
                    initialPropId = savedPropId;
                } else if (props.length > 0) {
                    initialPropId = (props.find(p => p.is_active) || props[0]).id;
                }
                setSelectedProperty(initialPropId);

                let initialDateFilter = 'today';
                const savedView = getPref('default_report_view');
                if (savedView === 'twice_a_month') {
                    const day = new Date().getDate();
                    initialDateFilter = day <= 15 ? 'firstHalf' : 'secondHalf';
                } else if (savedView === 'month') {
                    initialDateFilter = 'month';
                }
                setDateFilter(initialDateFilter || 'today');

                hasInitialized.current = true;
                await fetchReportData(initialPropId, '', initialDateFilter || 'today', { start: '', end: '' }, props);

            } catch (err) {
                console.error("Init Error:", err);
            } finally {
                setIsLoading(false);
            }
        };

        if (user) {
            initializeScreen();
        }
    }, [user]);

    useEffect(() => {
        if (userTimezonePref) {
            if (userTimezone !== userTimezonePref) setUserTimezone(userTimezonePref);
            return;
        }
        if (selectedProperty) {
            const prop = properties.find(p => p.id === parseInt(selectedProperty));
            if (prop && prop.timezone) {
                if (userTimezone !== prop.timezone) setUserTimezone(prop.timezone);
                return;
            }
        }
        if (hasInitialized.current && !userTimezonePref && userTimezone !== 'America/Los_Angeles') {
            setUserTimezone('America/Los_Angeles');
        }
    }, [userTimezonePref, selectedProperty, properties, userTimezone]);

    const formatTime = (isoString) => {
        if (!isoString) return '-';
        try {
            const zonedDate = toZonedTime(isoString, userTimezone);
            return format(zonedDate, 'hh:mm a', { timeZone: userTimezone });
        } catch (e) {
            return '-';
        }
    };

    useEffect(() => {
        setSelectedUser('');
        const fetchUsersForProperty = async () => {
            if (!selectedProperty) {
                setUsersForFilter([]);
                return;
            }
            try {
                const filters = { propertyId: selectedProperty };
                const users = await api.getWorkforce(filters);
                const trackableUsers = users.filter(u => u.role !== 'SuperAdmin');
                setUsersForFilter(trackableUsers);
            } catch (err) {
                setUsersForFilter([]);
            }
        };
        fetchUsersForProperty();
    }, [selectedProperty]); 

    const handleSendReport = async () => {
        if (!selectedProperty) { toast.error('Please select a specific property to send the report.'); return; }
        const currentProperty = properties.find(p => p.id === parseInt(selectedProperty));
        
        const doc = generateReportPDF('COMBINED', {
            summary, timeEntries, currentProperty, dateFilter, customDate, payrollData, user, timeZone: userTimezone
        });

        if (doc) {
            const pdfData = doc.output('datauristring');
            const toastId = toast.loading('Sending report...');
            api.sendTimeEntries({ propertyId: selectedProperty, propertyName: currentProperty?.name, pdfData: pdfData })
            .then((res) => {
                toast.dismiss(toastId);
                toast.success(res.message);
            })
            .catch((err) => {
                toast.dismiss(toastId);
                toast.error(err.message, { duration: 6000 });
            });
        }
    };

    const handleOpenEditModal = (entry) => { setEditingEntry(entry); setIsEditModalOpen(true); };
    const handleCloseEditModal = () => { setIsEditModalOpen(false); setEditingEntry(null); };

    const isEditFormValid = useMemo(() => {
        if (!editingEntry) return false;
        if (!editingEntry.notes || editingEntry.notes.trim().length === 0) return false;
        return true; 
    }, [editingEntry]);

    const handleSaveEntry = async () => {
        if (!editingEntry.notes) { toast.error('Notes are required.'); return; }
        if (!editForm.date || !editForm.startTime || !editForm.endTime) { 
            toast.error("Please provide Date, Start Time, and End Time."); return; 
        }

        const startStr = `${editForm.date}T${editForm.startTime}`;
        const endStr = `${editForm.date}T${editForm.endTime}`;
        
        const finalStartUTC = fromZonedTime(startStr, userTimezone);
        const finalEndUTC = fromZonedTime(endStr, userTimezone);

        if (finalEndUTC < finalStartUTC) { 
            toast.error('End time cannot be before start time.'); 
            return; 
        }

        const diffInHours = (finalEndUTC - finalStartUTC) / (1000 * 60 * 60);
        if (diffInHours > 12) {
            toast.error(`Shift cannot exceed 12 hours (${diffInHours.toFixed(2)}h).`);
            return;
        }

        const serverStartString = format(finalStartUTC, 'yyyy-MM-dd HH:mm:ss', { timeZone: SERVER_TIMEZONE });
        const serverEndString = format(finalEndUTC, 'yyyy-MM-dd HH:mm:ss', { timeZone: SERVER_TIMEZONE });

        const updatedEntry = { 
            ...editingEntry, 
            start_time: serverStartString,
            end_time: serverEndString 
        };

        try {
            await api.updateTimeEntry(updatedEntry.id, updatedEntry);
            toast.success('Entry Updated Successfully!');
            handleCloseEditModal();
            fetchReportData(); 
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleSaveNewEntry = async () => {
        if (!addForm.employeeId || !addForm.date || !addForm.startTime || !addForm.endTime || !addForm.notes) {
            toast.error("Please fill out all fields.");
            return;
        }

        const startStr = `${addForm.date}T${addForm.startTime}`;
        const endStr = `${addForm.date}T${addForm.endTime}`;

        const startUTC = fromZonedTime(startStr, userTimezone);
        const endUTC = fromZonedTime(endStr, userTimezone);

        if (endUTC < startUTC) {
            toast.error("End time cannot be before start time.");
            return;
        }

        const serverStart = format(startUTC, 'yyyy-MM-dd HH:mm:ss', { timeZone: SERVER_TIMEZONE });
        const serverEnd = format(endUTC, 'yyyy-MM-dd HH:mm:ss', { timeZone: SERVER_TIMEZONE });

        try {
            const payload = {
                propertyId: selectedProperty,
                employeeId: addForm.employeeId,
                startTime: serverStart,
                endTime: serverEnd,
                notes: addForm.notes
            };
            
            await api.createTimeEntry(payload); 
            toast.success('Time Entry saved successfully!');
            setIsAddEntryModalOpen(false);
            setAddForm({ employeeId: '', date: '', startTime: '', endTime: '', notes: '' });
            fetchReportData(); 
        } catch (err) {
            toast.error(err.message || 'Failed to create time entry');
        }
    };
    
    const getFilterSummaryText = () => {
        const propName = selectedProperty ? properties.find(p => p.id === parseInt(selectedProperty))?.name : 'All Properties';
        const userName = selectedUser ? usersForFilter.find(u => u.id === parseInt(selectedUser))?.first_name : 'All Users';
        return `${propName} • ${userName} • ${dateFilter}`;
    };

    const totalWorkHours = useMemo(() => {
        if (!summary) return "0h 0m";
        const totalMinutes = Object.values(summary).reduce((acc, employeeSummary) => {
            const timeString = employeeSummary.total_hours || "0h 0m";
            const hourMatch = timeString.match(/(\d+)\s*h/);
            const minuteMatch = timeString.match(/(\d+)\s*m/);
            return acc + ((hourMatch ? parseInt(hourMatch[1], 10) : 0) * 60) + (minuteMatch ? parseInt(minuteMatch[1], 10) : 0);
        }, 0);
        return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
    }, [summary]);

    if (isLoading) { return <div className="loading-screen"><Loader2 className="spin" size={48} /></div> }

    return (
        <div className="reports-page-container">
            <div className="page-header no-print">
                <h1 className="page-title">Reports</h1>
                <div className="page-header-actions">
                    {canEdit && (
                        <button 
                            onClick={() => setIsAddEntryModalOpen(true)} 
                            className="icon-action-button" 
                            title="Add Missing Time Entry"
                            disabled={!selectedProperty} 
                            style={{ opacity: !selectedProperty ? 0.5 : 1 }}
                        >
                            <Plus size={18} />
                        </button>
                    )}

                    <button onClick={() => setIsSummaryModalOpen(true)} className="icon-action-button" title="Generate PDF" disabled={!summary || Object.keys(summary).length === 0}><Printer size={18} /></button>
                    {canSendToAccountant && (<button onClick={handleSendReport} className="icon-action-button" title="Send Report" disabled={!summary || Object.keys(summary).length === 0 || !selectedProperty}><Send size={18} /></button>)}                
                    <button 
                        onClick={() => setIsPayrollModalOpen(true)} 
                        className="icon-action-button" 
                        title="Split Payroll"
                        disabled={!summary || Object.keys(summary).length === 0 || !selectedProperty || !isPayrollPeriod} 
                    >
                        <Settings2 size={18} />
                    </button>         
                </div>
            </div>

            {/* Mobile Filter Toggle */}
            <div className="filter-bar mobile-only" onClick={() => setIsFilterSheetOpen(true)}>
                <Filter size={16} />
                <span>{getFilterSummaryText()}</span>
                <ChevronDown size={20} />
            </div>

            {/* Desktop Filters */}
            <div className="card no-print filter-card desktop-only">
                <div className="report-filters">
                    <div className="filter-group">
                        <label>Property</label>
                        <select className="form-input" value={selectedProperty} onChange={(e) => { setSelectedProperty(e.target.value); }}>
                            {canViewAll && <option value="">All Properties</option>}
                            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>User</label>
                        <select 
                            className="form-input" 
                            value={selectedUser} 
                            onChange={e => setSelectedUser(e.target.value)}
                            disabled={!selectedProperty}
                            style={{ cursor: !selectedProperty ? 'not-allowed' : 'pointer', opacity: !selectedProperty ? 0.6 : 1 }}
                        >
                            <option value="">{selectedProperty ? "All Users" : "Select Property First"}</option>
                            {usersForFilter.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                        </select>
                    </div>
                    <div className="filter-group date-range-group">
                        <div className="date-filter-options">
                            <button className={dateFilter === 'firstHalf' ? 'active' : ''} onClick={() => setDateFilter('firstHalf')}>1st-15th</button>
                            <button className={dateFilter === 'secondHalf' ? 'active' : ''} onClick={() => setDateFilter('secondHalf')}>16th-End</button>
                            <button className={dateFilter === 'today' ? 'active' : ''} onClick={() => setDateFilter('today')}>Today</button>
                            <button className={dateFilter === 'month' ? 'active' : ''} onClick={() => setDateFilter('month')}>This Month</button>
                            <button className={dateFilter === 'custom' ? 'active' : ''} onClick={() => setDateFilter('custom')}>Custom</button>
                        </div>
                        {dateFilter === 'custom' && (
                            <div className="custom-date-inputs">
                                <input type="date" value={customDate.start} onChange={e => setCustomDate({...customDate, start: e.target.value})} className="form-input"/>
                                <span>to</span>
                                <input type="date" value={customDate.end} min={customDate.start} onChange={e => setCustomDate({...customDate, end: e.target.value})} className="form-input"/>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Filter Sheet */}
            {isFilterSheetOpen && (
                <div className="filter-sheet-overlay mobile-only" onClick={() => setIsFilterSheetOpen(false)}>
                    <div className="filter-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="filter-sheet-header">
                            <h3>Filters</h3>
                            <button onClick={() => setIsFilterSheetOpen(false)} className="icon-button">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="report-filters">
                            <div className="filter-group">
                                <label>Property</label>
                                <select className="form-input" value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)}>
                                    {canViewAll && <option value="">All Properties</option>}
                                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label>User</label>
                                <select 
                                    className="form-input" 
                                    value={selectedUser} 
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    disabled={!selectedProperty}
                                >
                                    <option value="">{selectedProperty ? "All Users" : "Select Property First"}</option>
                                    {usersForFilter.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                                </select>
                            </div>
                            <div className="filter-group date-range-group">
                                <label>Date Range</label>
                                <div className="date-filter-options">
                                    <button className={dateFilter === 'firstHalf' ? 'active' : ''} onClick={() => { setDateFilter('firstHalf'); setIsFilterSheetOpen(false); }}>1st-15th</button>
                                    <button className={dateFilter === 'secondHalf' ? 'active' : ''} onClick={() => { setDateFilter('secondHalf'); setIsFilterSheetOpen(false); }}>16th-End</button>
                                    <button className={dateFilter === 'today' ? 'active' : ''} onClick={() => { setDateFilter('today'); setIsFilterSheetOpen(false); }}>Today</button>
                                    <button className={dateFilter === 'month' ? 'active' : ''} onClick={() => { setDateFilter('month'); setIsFilterSheetOpen(false); }}>This Month</button>
                                    <button className={dateFilter === 'custom' ? 'active' : ''} onClick={() => setDateFilter('custom')}>Custom</button>
                                </div>
                                {dateFilter === 'custom' && (
                                    <div className="custom-date-inputs">
                                        <input type="date" value={customDate.start} onChange={e => setCustomDate({...customDate, start: e.target.value})} className="form-input"/>
                                        <input type="date" value={customDate.end} min={customDate.start} onChange={e => setCustomDate({...customDate, end: e.target.value})} className="form-input"/>
                                    </div>
                                )}
                            </div>
                            <div className="filter-group button-group">
                                <button onClick={() => setIsFilterSheetOpen(false)} className="action-button">Done</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {!summary && !isGenerating && ( <div className="placeholder-text">No data found for these filters.</div> )}
            
            {summary && (
                <div className="report-data-container" style={{ opacity: isGenerating ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                    <div className="card summary-section">
                        <h2 className="section-title" title="Total hours..." style={{ cursor: 'help' }} >
                            <Info size={18} />
                            <span>Total Hours - {totalWorkHours}</span>
                            {isGenerating && <Loader2 className="spin" size={16} style={{marginLeft: 'auto'}}/>}
                        </h2>
                        <div className="summary-grid">
                            {Object.keys(summary).length > 0 && Object.values(summary).map((s) => (
                                <div key={s.employee_name} className="summary-item">
                                    <span className="summary-employee-name">{s.employee_name}</span>
                                    <div className="summary-hours">
                                        <Clock size={14} /> 
                                        <span>{s.total_hours}</span>
                                        {s.overtime_hours && (
                                            <span style={{ 
                                                marginLeft: '8px', 
                                                color: '#ef4444', 
                                                fontWeight: '600', 
                                                fontSize: '0.9em' 
                                            }}>
                                                OT: {s.overtime_hours}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {timeEntries.length > 0 && (
                        <div className="card report-container">
                            <h2 className="section-title">Detailed Time Records</h2>
                            <div className="detailed-list mobile-only">
                                {timeEntries.map(entry => {
                                    const isCancelled = entry.start_time === entry.end_time;
                                    return (
                                        <React.Fragment key={entry.id}>
                                            <div className={`detailed-list-item ${isCancelled ? 'cancelled-entry-row' : ''}`}>
                                                <div className="list-item-main-content" onClick={() => handleToggleExpand(entry.id)}>
                                                    <div className="list-item-row">
                                                        <span className="list-item-employee">
                                                            {entry.employee_name}
                                                            <span className="employee-property-acronym">
                                                                {generateAcronym(entry.property_name)}
                                                            </span>
                                                        </span>
                                                        <span className="list-item-duration" style={{display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                                                            {isCancelled ? <span className="cancelled-badge">VOID</span> : (
                                                                entry.meal_deducted > 0 ? (
                                                                    <>
                                                                        <span style={{ fontSize: '0.75rem', color: '#64748b', textDecoration: 'line-through' }}>{entry.duration}</span>
                                                                        <span style={{ fontSize: '0.7rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '2px', margin:'2px 0' }}>
                                                                            <Coffee size={10}/> -{entry.meal_deducted}m break
                                                                        </span>
                                                                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{entry.payable_duration}</span>
                                                                    </>
                                                                ) : (
                                                                    <span>{entry.payable_duration || '-'}</span>
                                                                )
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="list-item-row sub-details">
                                                        <span>{entry.date}</span>
                                                        <span style={isCancelled ? { textDecoration: 'line-through', color: '#991b1b' } : {}}>
                                                            {formatTime(entry.start_time)}
                                                            {' → '}
                                                            {entry.end_time ? formatTime(entry.end_time) : 'Now'}
                                                        </span>
                                                        {isCancelled ? (
                                                            <span className="status-badge cancelled">Overridden</span>
                                                        ) : (
                                                            <span className={`status-badge ${entry.status === 'In Progress' ? 'in-progress' : 'completed'}`}>{entry.status}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {canEdit && (
                                                    <button className="icon-button" onClick={(e) => { e.stopPropagation(); handleOpenEditModal(entry); }}>
                                                        <Edit size={18} />
                                                    </button>
                                                )}
                                            </div>
                                            {expandedEntryId === entry.id && (
                                                <div className="edit-history-content mobile-history">
                                                    <h4>Edit History</h4>
                                                    <p><strong>Last Edit Type:</strong> {entry.last_edit_type || 'SYSTEM_GENERATED'}</p>
                                                    <p><strong>Notes:</strong> {entry.notes || 'N/A'}</p>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                            <table className="data-table desktop-only">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th>Date</th>
                                        <th>Employee</th>
                                        <th>Property</th>
                                        <th>Start Time</th>
                                        <th>End Time</th>
                                        <th>Duration</th>
                                        <th>Status</th>
                                        {canEdit && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {timeEntries.map(entry => {
                                        const isCancelled = entry.start_time === entry.end_time;
                                        return (
                                            <React.Fragment key={entry.id}>
                                                <tr className={isCancelled ? "cancelled-entry-row" : ""}>
                                                    <td>
                                                        <button onClick={() => handleToggleExpand(entry.id)} className="icon-button">
                                                            {expandedEntryId === entry.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                        </button>
                                                    </td>
                                                    <td data-label="Date"><span>{entry.date}</span></td>
                                                    <td data-label="Employee"><span>{entry.employee_name}</span></td>
                                                    <td data-label="Property"><span>{entry.property_name}</span></td>
                                                    <td data-label="Start Time"><span>{formatTime(entry.start_time)}</span></td>
                                                    <td data-label="End Time">
                                                        {isCancelled ? (
                                                            <span style={{ textDecoration: 'line-through', color: '#991b1b' }}>
                                                                {entry.end_time ? formatTime(entry.end_time) : '-'}
                                                            </span>
                                                        ) : (
                                                            <span>{entry.end_time ? formatTime(entry.end_time) : '-'}</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Duration">
                                                        {isCancelled ? (
                                                            <span className="cancelled-badge">VOID</span>
                                                        ) : (
                                                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                                                {entry.meal_deducted > 0 ? (
                                                                    <>
                                                                        <span style={{ fontSize: '0.85rem', color: '#94a3b8', textDecoration: 'line-through' }}>{entry.duration}</span>
                                                                        <span style={{ fontSize: '0.75rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px', margin: '2px 0' }}>
                                                                            <Coffee size={12}/> -{entry.meal_deducted}m break
                                                                        </span>
                                                                        <span style={{ fontWeight: '700', color: '#0f172a' }}>{entry.payable_duration}</span>
                                                                    </>
                                                                ) : (
                                                                    <span style={{ fontWeight: '600', color: '#0f172a' }}>{entry.payable_duration || '-'}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td data-label="Status">
                                                        {isCancelled ? (
                                                            <span className="status-badge cancelled">Overridden</span>
                                                        ) : (
                                                            <span className={`status-badge ${entry.status === 'In Progress' ? 'in-progress' : 'completed'}`}>{entry.status}</span>
                                                        )}
                                                    </td>
                                                    {canEdit && (<td data-label="Actions"><button onClick={() => handleOpenEditModal(entry)} className="icon-button"><Edit size={16} /></button></td>)}
                                                </tr>
                                                {expandedEntryId === entry.id && (
                                                    <tr className="edit-history-row"><td colSpan="9"><div className="edit-history-content"><h4>Edit History</h4><p><strong>Last Edit Type:</strong> {entry.last_edit_type || 'SYSTEM_GENERATED'}</p><p><strong>Notes:</strong> {entry.notes || 'N/A'}</p></div></td></tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            
            {/* --- MODAL: EDIT EXISTING ENTRY --- */}
            {isEditModalOpen && editingEntry && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h2>Edit Entry for {editingEntry.employee_name}</h2>
                        
                        <p className="modal-subtitle" style={{ marginBottom: '20px', color: '#64748b', fontSize: '0.95rem' }}>
                            Update the specific shift times below. Ensure you provide a reason for the adjustment in the notes.
                        </p>

                        <div className="form-group">
                            <label className="form-label">Date</label>
                            <input 
                                type="date" 
                                className="form-input" 
                                value={editForm.date} 
                                onChange={e => setEditForm({ ...editForm, date: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Shift Hours (Start & End Time)</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input 
                                    type="time" 
                                    className="form-input" 
                                    value={editForm.startTime} 
                                    onChange={e => setEditForm({ ...editForm, startTime: e.target.value })}
                                />
                                <input 
                                    type="time" 
                                    className="form-input" 
                                    value={editForm.endTime} 
                                    onChange={e => setEditForm({ ...editForm, endTime: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Notes (Required)</label>
                            <textarea 
                                className="form-input" 
                                value={editingEntry.notes || ''} 
                                onChange={e => setEditingEntry({...editingEntry, notes: e.target.value})} 
                                rows="3"
                                placeholder="Reason for adjustment..."
                            ></textarea>
                        </div>

                        <div className="modal-actions">
                            <button onClick={handleCloseEditModal} className="button button-secondary">Cancel</button>
                            <button 
                                onClick={handleSaveEntry} 
                                className="button action-button"
                                disabled={!isEditFormValid}
                                title={!isEditFormValid ? "Enter valid Notes to save." : ""}
                                style={{ opacity: !isEditFormValid ? 0.5 : 1, cursor: !isEditFormValid ? 'not-allowed' : 'pointer' }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL: ADD NEW ENTRY --- */}
            {isAddEntryModalOpen && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h2>Add Missing Time Entry</h2>
                        <p className="modal-subtitle" style={{ marginBottom: '15px', color: '#64748b', fontSize: '0.9rem' }}>
                            Create a manual time entry for an employee.
                        </p>

                        <div className="form-group">
                            <label className="form-label">Employee</label>
                            <select 
                                className="form-input"
                                value={addForm.employeeId}
                                onChange={e => setAddForm({ ...addForm, employeeId: e.target.value })}
                            >
                                <option value="">Select Employee...</option>
                                {usersForFilter.map(u => (
                                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Date</label>
                            <input 
                                type="date" 
                                className="form-input" 
                                value={addForm.date} 
                                onChange={e => setAddForm({ ...addForm, date: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Shift Hours (Start & End Time)</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <input 
                                    type="time" 
                                    className="form-input" 
                                    value={addForm.startTime} 
                                    onChange={e => setAddForm({ ...addForm, startTime: e.target.value })}
                                />
                                <input 
                                    type="time" 
                                    className="form-input" 
                                    value={addForm.endTime} 
                                    onChange={e => setAddForm({ ...addForm, endTime: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Notes (Required)</label>
                            <textarea 
                                className="form-input" 
                                value={addForm.notes} 
                                onChange={e => setAddForm({...addForm, notes: e.target.value})} 
                                rows="2"
                                placeholder="Reason for manual entry (e.g. Employee forgot to punch)"
                            ></textarea>
                        </div>

                        <div className="modal-actions">
                            <button onClick={() => setIsAddEntryModalOpen(false)} className="button button-secondary">Cancel</button>
                            <button 
                                onClick={handleSaveNewEntry} 
                                className="button action-button"
                                disabled={!addForm.employeeId || !addForm.date || !addForm.startTime || !addForm.endTime || !addForm.notes}
                                style={{ opacity: (!addForm.employeeId || !addForm.date || !addForm.startTime || !addForm.endTime || !addForm.notes) ? 0.5 : 1 }}
                            >
                                Save Entry
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SummaryReportModal
                isOpen={isSummaryModalOpen}
                onClose={() => setIsSummaryModalOpen(false)}
                summary={summary}
                timeEntries={timeEntries}
                properties={properties}
                selectedProperty={selectedProperty}
                dateFilter={dateFilter}
                customDate={customDate}
                payrollData={payrollData}
                user={user}
                userTimezone={userTimezone}
            />

            {isPayrollModalOpen && (
                <SplitPayrollModal
                    onClose={() => {
                        setIsPayrollModalOpen(false);
                        fetchReportData();
                    }}
                    summaryData={summary}
                    propertyId={selectedProperty}
                    startDate={reportDateRange.start}
                    endDate={reportDateRange.end}
                    periodType={periodType}
                />
            )}
        </div>
    );
};

export default ReportScreen;